import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicon from 'react-native-vector-icons/Ionicons';
import { CxcSittingView, shortCxcDeadline } from '../../utils/cxcRegistration';

interface CxcBannerProps {
  sittings: CxcSittingView[];
  /** "Year 10 · Sharanya B" under the title. */
  subtitle?: string;
  backgroundColor: string;
  onRegister: () => void;
  /**
   * Desktop web: the Register button sits in the header row and each sitting stays on one
   * line. Phones (the app, and the web below 768 px) stack everything.
   */
  wide?: boolean;
}

/**
 * The Home "CXC / CSEC registration" card, shared by the app and the web so they stay alike.
 *
 * It used to be one row - icon | text | button - which on a phone left the text a narrow
 * column: the title wrapped over three lines with OPEN floating beside it, each deadline
 * broke across lines, and the icon and button floated mid-way down a tall block (QA).
 */
export function CxcBanner({ sittings, subtitle, backgroundColor, onRegister, wide = false }: CxcBannerProps) {
  const registerButton = (
    <TouchableOpacity
      style={[styles.registerBtn, wide && styles.registerBtnWide]}
      onPress={onRegister}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel="Register for CXC examinations"
    >
      <Text style={styles.registerText}>Register now</Text>
      <Ionicon name="arrow-forward" size={16} color="#1D4ED8" />
    </TouchableOpacity>
  );

  return (
    <View style={[styles.card, { backgroundColor, shadowColor: backgroundColor }]}>
      <View style={styles.header}>
        <View style={styles.icon}>
          <Ionicon name="document-text-outline" size={22} color="#FFF" />
        </View>
        <View style={styles.titleBox}>
          <Text style={styles.title} numberOfLines={2}>CXC / CSEC registration</Text>
          {!!subtitle && <Text style={styles.subtitle} numberOfLines={1}>{subtitle}</Text>}
        </View>
        <View style={styles.openBadge}>
          <View style={styles.openDot} />
          <Text style={styles.openText}>OPEN</Text>
        </View>
        {wide && registerButton}
      </View>

      <View style={styles.panel}>
        {sittings.map((sitting, index) => (
          <View
            key={sitting.id}
            style={[styles.sitting, wide && styles.sittingWide, index > 0 && styles.sittingDivider]}
          >
            <View style={[styles.tag, !sitting.isOpen && styles.tagClosed]}>
              <Text style={[styles.tagText, !sitting.isOpen && styles.textClosed]} numberOfLines={1}>
                {sitting.label}
              </Text>
            </View>
            <Text style={[styles.deadline, wide ? styles.deadlineWide : styles.deadlineStacked, !sitting.isOpen && styles.textClosed]}>
              {sitting.isOpen ? 'Closes ' : 'Closed on '}
              <Text style={styles.deadlineDate}>{shortCxcDeadline(sitting.deadline)}</Text>
            </Text>
          </View>
        ))}
      </View>

      {!wide && registerButton}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 20,
    marginBottom: 24,
    borderRadius: 20,
    padding: 16,
    elevation: 4,
    shadowOpacity: 0.3,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  icon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleBox: { flex: 1, minWidth: 0 },
  title: { color: '#FFF', fontSize: 16, fontWeight: '800', lineHeight: 20 },
  subtitle: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '600', marginTop: 3 },
  openBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(16, 185, 129, 0.25)',
    borderWidth: 1,
    borderColor: 'rgba(52, 211, 153, 0.6)',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  openDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#34D399', marginRight: 5 },
  openText: { color: '#FFF', fontSize: 10, fontWeight: '800', letterSpacing: 0.4 },

  panel: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 14,
    paddingHorizontal: 12,
    marginTop: 14,
  },
  // Phone: the sitting on one line, its deadline on the next, with room between them.
  sitting: { paddingVertical: 12 },
  sittingWide: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  sittingDivider: { borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.18)' },
  tag: {
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.38)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagClosed: { backgroundColor: 'rgba(239, 68, 68, 0.25)', borderColor: 'rgba(239, 68, 68, 0.5)' },
  tagText: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  deadline: { color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: '500' },
  deadlineStacked: { marginTop: 8 },
  deadlineWide: { textAlign: 'right' },
  deadlineDate: { color: '#FFF', fontWeight: '800' },
  textClosed: { color: '#FEE2E2' },

  registerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FFF',
    borderRadius: 14,
    paddingVertical: 12,
    marginTop: 14,
  },
  registerBtnWide: { marginTop: 0, paddingHorizontal: 18, paddingVertical: 10 },
  registerText: { color: '#1D4ED8', fontSize: 15, fontWeight: '800' },
});
